import React, { useState } from 'react';
import { Edit2, AlertTriangle, ArrowUpDown, Plus, Search, X, PlusCircle } from 'lucide-react';
import Card from '../../Components/Ui/Card';
import ActionDropdown from '../../Components/Ui/ActionDropdown';
import Table from '../../Components/Ui/Table';
import Button from '../../Components/Ui/Button';
import Badge from '../../Components/Ui/Badge';
import StockIndicator from '../../Components/Ui/StockIndicator';
import { inventoryItemsData } from '../../Data/Dummydata';

const Inventory = () => {
  const [inventoryItems, setInventoryItems] = useState(inventoryItemsData);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [stockStatus, setStockStatus] = useState('');

  // Function: Update stock in bulk (dummy functionality)
  const handleBulkUpdate = () => {
    const updatedItems = inventoryItems.map((item) => ({
      ...item,
      stock: item.stock + 10, // Add 10 to each item stock
    }));
    setInventoryItems(updatedItems);
    alert('Stock updated for all items (added 10 units each)');
  };

  // Function: Transfer stock (dummy functionality)
  const handleStockTransfer = () => {
    alert('Stock transfer action triggered! (You can add a form or modal here)');
  };

  // Filter logic
  const filteredItems = inventoryItems.filter((item) => {
    const matchesSearch =
      searchQuery.length < 3 ||
      item.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.variant.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = !selectedCategory || item.category === selectedCategory;

    const matchesLocation = !selectedLocation || item.location === selectedLocation;

    const matchesStockStatus =
      !stockStatus ||
      (stockStatus === 'in_stock' && item.stock > item.reorderPoint) ||
      (stockStatus === 'low_stock' && item.stock <= item.reorderPoint && item.stock > 0) ||
      (stockStatus === 'out_of_stock' && item.stock === 0);

    return matchesSearch && matchesCategory && matchesLocation && matchesStockStatus;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const suggestions =
    searchQuery.length >= 3
      ? inventoryItems.filter((item) => {
          const query = searchQuery.toLowerCase();
          return (
            item.productName.toLowerCase().includes(query) ||
            item.sku.toLowerCase().includes(query) ||
            item.variant.toLowerCase().includes(query)
          );
        })
      : [];

  const uniqueCategories = [...new Set(inventoryItems.map((item) => item.category))];

  const uniqueLocations = [...new Set(inventoryItems.map((item) => item.location))];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold ">Inventory Management</h2>
        <div className="flex flex-wrap gap-2">
          <Button leftIcon={<ArrowUpDown size={16} />} onClick={handleStockTransfer} size="md">
            Transfer Stock
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={handleBulkUpdate} size="md">
            Update Stock
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          {/* Search and Filters */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search inventory..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer hover:text-secondary"
                onClick={() => setSearchQuery('')}
              />
            )}
            {searchQuery.length >= 3 && (
              <ul className="absolute z-10 mt-1 w-full rounded-md bg-white border border-gray-200 shadow-sm max-h-48 overflow-auto">
                {suggestions.length > 0 ? (
                  suggestions.map((item, idx) => (
                    <li
                      key={idx}
                      onClick={() => setSearchQuery(item.productName)}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {item.productName} <span className="text-gray-400">({item.sku})</span>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-2 text-gray-500 text-sm cursor-default">
                    No results found
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="flex space-x-2 flex-wrap">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="border py-2 border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary focus:outline-none focus:ring-1 "
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((cat, index) => (
                <option key={index} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={selectedLocation}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
                setCurrentPage(1);
              }}
              className="border py-2 border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary focus:outline-none focus:ring-1"
            >
              <option value="">All Locations</option>
              {uniqueLocations.map((loc, index) => (
                <option key={index} value={loc}>
                  {loc}
                </option>
              ))}
            </select>

            <select
              value={stockStatus}
              onChange={(e) => {
                setStockStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="border py-2 border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary focus:outline-none focus:ring-1"
            >
              <option value="">Stock Status</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>
        </div>

        <Table
          columns={[
            {
              header: 'Product',
              accessor: (row) => (
                <div>
                  <p className="font-medium">{row.productName}</p>
                  <p className="text-sm text-gray-500">{row.variant}</p>
                </div>
              ),
            },
            { header: 'SKU', accessor: 'sku' },

            {
              header: 'Stock Information',
              accessor: (row) => {
                const isOutOfStock = row.stock === 0;
                return (
                  <div className="flex flex-col items-center">
                    <StockIndicator
                      currentStock={row.stock}
                      maxStock={row.maxStock}
                      reorderPoint={row.reorderPoint}
                    />
                    <span
                      className={`text-sm font-semibold mt-1 ${
                        isOutOfStock ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {isOutOfStock ? 'Out of Stock' : 'In Stock'}
                    </span>
                    <span className="text-xs text-gray-500">{row.stock} units</span>
                  </div>
                );
              },
              className: 'text-center',
            },

            {
              header: 'Unit Price',
              accessor: (row) => <span className="font-medium">${row.unitPrice.toFixed(2)}</span>,
              className: 'text-right',
            },
            {
              header: 'Location',
              accessor: (row) => <Badge variant="primary">{row.location}</Badge>,
              className: 'text-center',
            },
            {
              header: 'Last Restocked',
              accessor: (row) =>
                new Date(row.lastRestocked).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }),
            },
            {
              header: 'Actions',
              accessor: (row) => (
                <ActionDropdown
                  actions={[
                    {
                      label: 'Restock',
                      icon: PlusCircle,
                      onClick: () => {
                        const qty = prompt(`Enter restock quantity for ${row.productName}:`);
                        const quantity = parseInt(qty);
                        if (!isNaN(quantity) && quantity > 0) {
                          const updated = inventoryItems.map((item) =>
                            item.id === row.id
                              ? {
                                  ...item,
                                  stock: item.stock + quantity,
                                  lastRestocked: new Date().toISOString(),
                                }
                              : item,
                          );
                          setInventoryItems(updated);
                          alert(`Restocked ${quantity} units of ${row.productName}`);
                        } else {
                          alert('Please enter a valid number');
                        }
                      },
                    },
                    {
                      label: 'Update',
                      icon: Edit2,
                      onClick: () => alert('Update product details (future form)'),
                    },
                  ]}
                />
              ),
            },
          ]}
          data={paginatedItems}
          keyExtractor={(row) => row.id}
        />

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length}{' '}
            items •
            <span className="text-warning font-medium">
              {filteredItems.filter((item) => item.stock < item.reorderPoint).length} low stock
            </span>
          </p>
          <div className="flex items-center space-x-2">
            <button
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`px-3 py-1 rounded-lg text-sm ${
                  currentPage === i + 1 ? 'bg-primary white-text' : 'border border-gray-300'
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </Card>

      <Card title="Inventory Alerts">
        <div className="space-y-4">
          <div className="flex items-center p-4 bg-error bg-opacity-10 rounded-lg">
            <AlertTriangle size={20} className="text-error mr-3" />
            <div>
              <h4 className="font-medium text-error">Critical Stock Alert</h4>
              <p className="text-sm text-gray-600">
                {inventoryItems.filter((item) => item.stock < item.reorderPoint).length} products
                are below reorder point
              </p>
            </div>
          </div>
          <div className="flex items-center p-4 bg-warning bg-opacity-10 rounded-lg">
            <AlertTriangle size={20} className="text-warning mr-3" />
            <div>
              <h4 className="font-medium text-warning">Low Stock Warning</h4>
              <p className="text-sm text-gray-600">
                {
                  inventoryItems.filter((item) => item.stock <= item.reorderPoint && item.stock > 0)
                    .length
                }{' '}
                products are running low on stock
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Inventory;
